import type { RulesModel } from '@modules/configuration/Rules/models/Rules';
import { faBullseye, faListCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@shared/utils/utils';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRulesReferenceDataEpoch } from '@modules/configuration/Rules/hooks/useRulesReferenceDataEpoch';
import { buildClientRuleInfo } from '../utils/clientRuleDisplay';

interface ClientRuleInfoDisplayProps {
  rule: RulesModel;
  className?: string;
}

/**
 * Condiciones: una línea por criterio (campo + operador + valor). Acción: párrafos de texto.
 * Memoizado por `rule` para no repetir build al colapsar/expandir la card.
 */
const ClientRuleInfoDisplayInner = ({ rule, className }: ClientRuleInfoDisplayProps) => {
  const { t } = useTranslation('rulesModule');
  const refDataEpoch = useRulesReferenceDataEpoch();

  const { conditions, matchAll, actionSummaryLines } = useMemo(
    () => buildClientRuleInfo(rule, t),
    [rule, t, refDataEpoch],
  );

  const hasActions = (rule.actions?.length ?? 0) > 0;
  const showConditionMode = conditions.length > 1;

  return (
    <div className={cn('space-y-4 text-sm', className)} data-testid="client-rule-info-display">
      <section className="space-y-2">
        <h4 className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
          <FontAwesomeIcon icon={faListCheck} className="h-4 w-4 shrink-0 text-muted-foreground" />
          {t('clientList.display.conditions')}
        </h4>

        {conditions.length === 0 ? (
          <p className="rounded-md border border-dashed border-border bg-muted/10 px-3 py-2.5 text-sm text-muted-foreground">
            {t('clientList.display.no_conditions')}
          </p>
        ) : (
          <div className="rounded-md border border-border bg-muted/5 p-3 sm:p-4">
            {showConditionMode && (
              <p className="mb-3 border-b border-border/70 pb-3 text-xs font-medium leading-snug text-muted-foreground">
                {matchAll ? t('clientList.display.condition_intro_all') : t('clientList.display.condition_intro_any')}
              </p>
            )}
            <ul className="m-0 list-none space-y-3 p-0" role="list">
              {conditions.map((row, i) => (
                <li key={row.id}>
                  <div
                    className={cn(
                      'flex items-center gap-3 rounded-md border border-border/70 bg-card px-3 py-2',
                      'transition-colors',
                    )}
                  >
                    {showConditionMode && (
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-md bg-muted/90 text-xs font-semibold tabular-nums text-muted-foreground"
                        aria-hidden
                      >
                        {i + 1}
                      </span>
                    )}
                    <p
                      className="min-w-0 flex-1 text-sm leading-snug text-foreground [text-wrap:pretty]"
                      title={`${row.fieldLabel} ${row.opLabel} ${row.valueDisplay}`}
                    >
                      <span className="font-semibold">{row.fieldLabel}</span>
                      <span className="mx-1.5 text-muted-foreground">{row.opLabel}</span>
                      <span className="rounded-sm bg-muted/50 px-1.5 py-0.5 font-mono text-[13px] text-foreground">
                        {row.valueDisplay}
                      </span>
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h4 className="inline-flex items-center gap-2 text-sm font-semibold text-foreground">
          <FontAwesomeIcon icon={faBullseye} className="h-4 w-4 shrink-0 text-muted-foreground" />
          {t('clientList.display.action')}
        </h4>

        {!hasActions ? (
          <p className="rounded-md border border-dashed border-border bg-muted/10 px-3 py-2.5 text-sm text-muted-foreground">
            {t('clientList.display.action_no')}
          </p>
        ) : (
          <div className="space-y-1.5 rounded-md border border-border bg-muted/5 px-3 py-2.5 text-sm leading-relaxed text-foreground sm:px-4 sm:py-3">
            {actionSummaryLines.map((line, i) => (
              <p key={i} className="min-w-0 break-words">
                {line}
              </p>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export const ClientRuleInfoDisplay = memo(ClientRuleInfoDisplayInner, (prev, next) => {
  return prev.rule === next.rule && prev.className === next.className;
});
ClientRuleInfoDisplayInner.displayName = 'ClientRuleInfoDisplay';
